//lib
import type { SpGroupType } from '@/utils/hooks/swr/group/useSPGroups';
//types
import type useIsGroup from '@/utils/hooks/user/useIsGroup';

import type { SocialProfileType } from '@/types/socialProfileType';

import { availableNetwork } from '@/lib/scheduler/config/availableNetwork';

import type { SchedulerFunnelSettingsType } from '@/components/composite/Scheduler/Funnel';

type UseGetCreationStatusReturnType = {
    profiles: SocialProfileType['id'][];
    creation_type: Extract<SchedulerFunnelSettingsType['type'], 'create' | 'create_reminder'>;
};

type UseGetFunnelCreationStatusParams = {
    activeGroup: SpGroupType;
    activeSocialProfile: SocialProfileType;
    isGroup: ReturnType<typeof useIsGroup>;
};

function getProfilesWithSchedulerPermission(profiles: SocialProfileType[]) {
    return profiles.filter((profile) => profile.permissions?.includes('scheduler'));
}

export function useGetFunnelCreationStatus({
    activeGroup,
    activeSocialProfile,
    isGroup
}: UseGetFunnelCreationStatusParams): UseGetCreationStatusReturnType {
    if (isGroup) {
        const availableProfilesFromGroup = activeGroup.social_profiles.filter((profile) => {
            return (
                availableNetwork.some((network) => network === profile.social_network_type) &&
                profile.active
            );
        });
        const profilesWithSchedulerPermission = getProfilesWithSchedulerPermission(
            availableProfilesFromGroup
        );

        if (profilesWithSchedulerPermission.length) {
            return {
                profiles: profilesWithSchedulerPermission.map((profile) => profile.id),
                creation_type: 'create'
            };
        }
        return {
            profiles: availableProfilesFromGroup.map((profile) => profile.id),
            creation_type: 'create_reminder'
        };
    }

    const availableProfile = availableNetwork.some(
        (network) => network === activeSocialProfile.social_network_type
    );
    if (!availableProfile) {
        return { profiles: null, creation_type: null };
    }

    const profilesWithSchedulerPermission = getProfilesWithSchedulerPermission([
        activeSocialProfile
    ]);
    if (profilesWithSchedulerPermission.length) {
        return {
            profiles: [activeSocialProfile.id],
            creation_type: 'create'
        };
    }
    return {
        profiles: [activeSocialProfile.id],
        creation_type: 'create_reminder'
    };
}
